import { Input, Component, OnInit, forwardRef, Self, Optional, SkipSelf, Host } from '@angular/core';
import { AbstractControl, ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

@Component({
  selector: 'smrts-combo-box',
  templateUrl: './combo-box.component.html',
  styleUrls: ['./combo-box.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ComboBoxComponent),
      multi: true
    }
  ]
})
export class ComboBoxComponent implements OnInit, ControlValueAccessor {

  @Input() formControlName: string;
  @Input() disabled: boolean = false;
  @Input() label: string = "";
  @Input() set optionList(value: string[] | Map<any, string>) { this.setOptionList(value); }

  private _optionList: string[] = [];
  private _enumMap = new Map<string, any>();
  private _reverseEnumMap = new Map<any, string>();

  selectedOptionList: string[] = [];
  displayValue: string | undefined;
  actualValue: any = '';

  control: AbstractControl | null | undefined;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    @Optional() @Host() @SkipSelf() private controlContainer: ControlContainer
  ) {
  }

  ngOnInit(): void {
    this.selectedOptionList = [...this._optionList];
    if (this.controlContainer && this.formControlName) {
      this.control = this.controlContainer.control?.get(this.formControlName);
    }
  }

  private setOptionList(value: string[] | Map<any, string>) {
    if (Array.isArray(value)) {
      this._optionList = value;
      this._enumMap.clear();
      this._reverseEnumMap.clear();
    } else if (value instanceof Map) {
      this._optionList = Array.from(value.values());
      this._enumMap = new Map(Array.from(value.entries()).map(([key, val]) => [val, key]));
      this._reverseEnumMap = new Map(Array.from(value.entries()).map(([key, val]) => [key, val]));
    }
    this.selectedOptionList = [...this._optionList];
  }
  
  private getEnumFromString(displayValue: string): any {
    if (this._enumMap.size > 0) {
      return this._enumMap.get(displayValue);
    }
    return displayValue;
  }
  
  private getStringFromEnum(actualValue: any): string | undefined {
    if (this._reverseEnumMap.size > 0) {
      return this._reverseEnumMap.get(actualValue);
    }
    return actualValue;
  }

  public writeValue(value: any): void {
    this.actualValue = value;
    this.displayValue = this.getStringFromEnum(value);
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onKey(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input) {
      const searchString = input.value;
      this.selectedOptionList = searchString ? this.search(searchString) : [...this._optionList];
    }
  }

  private search(searchString: string) {
    let filter = searchString.toLowerCase();
    return this._optionList.filter(option =>
      option.toLowerCase().startsWith(filter)
    );
  }

  public onSelectionChange(displayValue: string): void {
    this.displayValue = displayValue;
    this.actualValue = this.getEnumFromString(displayValue);
    this.onChange(this.actualValue);
    this.onTouched();
  }
}
